import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { api, ApiError } from '../api';
import { can } from '../auth/me';
import { useMe } from '../auth/useMe';
import { EVENT_SCOPES } from '../events/labels';
import { systemLabel, useSystems } from '../settings/systems';
import { useDebounced } from '../kits/KitEditor';
import { DeleteDialog } from './BlocksPage';
import { SectionNav } from './SectionNav';
import { ListTable } from './ListTable';
import { TemplateEditor } from './TemplateEditor';
import { BLOCKS_RESOURCE, KITS_RESOURCE, TEMPLATES_KEY, TEMPLATES_RESOURCE, type TemplateSummary } from './types';
import { leaveGuarded } from '../ui/unsaved';

export function TemplatesPage() {
  const { me } = useMe();
  const systems = useSystems().data?.systems ?? [];
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<string | null | undefined>(undefined);
  const [deleting, setDeleting] = useState<TemplateSummary | null>(null);
  const q = useDebounced(search.trim(), 250);
  const allowed = !!me && can(me, TEMPLATES_RESOURCE, 'view');
  const list = useQuery({
    queryKey: [...TEMPLATES_KEY, 'templates', q],
    enabled: allowed,
    placeholderData: keepPreviousData,
    queryFn: () => api<{ templates: TemplateSummary[]; truncated: boolean }>(`/api/templates/templates${q ? `?q=${encodeURIComponent(q)}` : ''}`),
  });
  if (!me) return null;
  // the section's menu entry leads here; a role without templates goes to the blocks or the kits it may see
  if (!allowed) return <Navigate to={can(me, BLOCKS_RESOURCE, 'view') ? '/szablony/bloki' : can(me, KITS_RESOURCE, 'view') ? '/szablony/zestawy' : '/'} replace />;
  const templates = list.data?.templates ?? [];

  return (
    <section className="page" data-feature-id="templates.templates">
      <div className="page-head">
        <h1>Szablony</h1>
        {can(me, TEMPLATES_RESOURCE, 'create') && editing === undefined && (
          <button className="btn primary" type="button" onClick={() => leaveGuarded(() => setEditing(null))}>
            Dodaj szablon
          </button>
        )}
      </div>
      <SectionNav me={me} />
      <p className="muted">Szablon wydarzenia to bloki i pozycje sprzętu z zakresem prac i listą kontrolną, np. „Koncert plenerowy standard”. Wstawisz go w sprzętówce wydarzenia.</p>

      {editing !== undefined && <TemplateEditor key={editing ?? 'new'} {...(editing !== null ? { templateId: editing } : {})} onClose={() => setEditing(undefined)} />}

      <div className="filters">
        <label className="field">
          <span>Szukaj</span>
          <input type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Nazwa szablonu" autoComplete="off" />
        </label>
      </div>
      {list.isLoading && <p className="muted" role="status">Wczytywanie…</p>}
      {list.error && <p className="alert" role="alert">{list.error instanceof ApiError ? list.error.message : 'Nie udało się wczytać szablonów'}</p>}
      {list.data && templates.length === 0 && <p className="muted">Nie znaleziono szablonów.</p>}
      {list.data?.truncated && <p className="notice">Pokazano pierwszych 200 szablonów. Zawęź wyszukiwanie, żeby zobaczyć pozostałe.</p>}
      <ListTable
        me={me}
        label="Szablony wydarzeń"
        resource={TEMPLATES_RESOURCE}
        rows={templates.map((t) => ({
          ...t,
          details: [
            t.scopes?.length ? `Zakres: ${t.scopes.map((s) => EVENT_SCOPES[s as keyof typeof EVENT_SCOPES] ?? s).join(', ')}` : '',
            t.systemId ? `System: ${systemLabel(systems, t.systemId)}` : '',
          ]
            .filter(Boolean)
            .join(' · '),
          counts: [t.blockCount, t.lineCount],
        }))}
        countLabels={['Bloków', 'Pozycji']}
        openLabel={(t) => `Otwórz: ${t.name}`}
        openText={can(me, TEMPLATES_RESOURCE, 'edit') ? 'Edytuj' : 'Pokaż'}
        onOpen={(t) => leaveGuarded(() => setEditing(t.id))}
        onDelete={(t) => setDeleting(t)}
      />
      {deleting && <DeleteDialog what="szablon" name={deleting.name} url={`/api/templates/templates/${deleting.id}?version=${deleting.version}`} onClose={() => setDeleting(null)} />}
    </section>
  );
}
